import {AsyncCollection} from '../async-collection/async-collection';
import {AsyncList} from '../async-list/async-list';

export namespace AsyncListBase {
    /**
     * Base structure interface that all async list structures must extend.
     * Ensures the structure has an `items` array for deduplication support.
     */
    export interface AsyncListStructure<T> {
        items: T[];
    }

    /**
     * Options for list-based async collections.
     * Extends AsyncCollection.ConstructorOptions with list-specific options.
     * Subclasses should extend this interface to add their own options.
     */
    export interface Options<T, STRUCTURE extends AsyncListStructure<T>> extends AsyncCollection.ConstructorOptions<STRUCTURE, number> {
        /**
         * Optional function to extract a unique identifier from an item.
         * When provided, enables safe refresh with deduplication.
         * During both fetch and refresh, if the same ID appears multiple times,
         * the latest fetched value is kept (ensuring fresh data).
         */
        getItemId?: (item: T) => string | number;
    }
}

/**
 * Abstract base class for async list collections.
 * Provides common functionality for list-based collections including:
 * - Item deduplication during fetch and refresh (when getItemId is provided)
 * - Safe refresh checks to prevent duplicate items
 * 
 * Subclasses must implement the AsyncList interface methods:
 * - getItems()
 * - hasMore()
 * - getFetchedItemsCount()
 * 
 * @typeParam T - The type of items in the list
 * @typeParam STRUCTURE - The internal structure type (must extend AsyncListStructure with items array)
 */
export abstract class AsyncListBase<T, STRUCTURE extends AsyncListBase.AsyncListStructure<T>>
    extends AsyncCollection<STRUCTURE, number>
    implements AsyncList<T> {

    protected readonly _getItemId?: (item: T) => string | number;

    constructor(options: AsyncListBase.Options<T, STRUCTURE>) {
        super(options);
        this._getItemId = options.getItemId;
    }

    // Abstract methods from AsyncList interface - subclasses must implement
    abstract getItems(): T[];
    abstract hasMore(): boolean;
    abstract getFetchedItemsCount(): number;

    /**
     * Override: List refresh is only safe if getItemId is provided (enables deduplication).
     * Without getItemId, iterative refresh can cause duplicate items when data shifts
     * between paginated fetches.
     */
    protected canSafelyRefresh(): boolean {
        return this._getItemId !== undefined;
    }

    /**
     * Deduplicates an array of items using getItemId.
     * If the same ID appears multiple times, the latest value is kept (freshest data).
     * If getItemId is not provided, returns the original array unchanged.
     * 
     * @param items - Array of items to deduplicate
     * @returns Deduplicated array (or original if getItemId not provided)
     */
    protected deduplicateItems(items: T[]): T[] {
        if (!this._getItemId) {
            return items;
        }
        const itemsMap = new Map<string | number, T>();
        for (const item of items) {
            itemsMap.set(this._getItemId(item), item);
        }
        return Array.from(itemsMap.values());
    }

    /**
     * Override: Process fetched values to deduplicate items when getItemId is provided.
     * This ensures deduplication happens automatically for both fetch and refresh operations.
     * @param value The structure returned from the fetch function
     * @returns The structure with deduplicated items
     */
    protected processValueAfterFetch(value: STRUCTURE): STRUCTURE {
        if (!this._getItemId) {
            return value;
        }
        return {
            ...value,
            items: this.deduplicateItems(value.items)
        };
    }
}
